/**
 * Module-level cache + boot projection for the gamification feature flags (#229).
 *
 * Mirrors `features/desktop/desktop-shell-flag.ts`: the gamification keys
 * ride the SAME `GET /api/v2/public/feature-flags` envelope that main.tsx
 * boot-fetches. main.tsx applies the raw map through
 * `applyGamificationFlagsFromBootFetch`, which rejects missing responses.
 * Active subscribers then share one low-frequency refresh loop so an operator
 * kill switch or activation reaches an already-open Quartel session.
 *
 * Rollout starts with every key OFF. With `gamification.enabled` false the
 * widget renders nothing at all. To avoid a flash of the chip on every load
 * (the boot fetch resolves after first paint), the last snapshot is persisted
 * to localStorage and read synchronously at init.
 */
import { fetchPublicFeatureFlagsRaw } from "@/features/pwa/lib/fetch-pwa-flags";

export const GAMIFICATION_ENABLED_KEY = "gamification.enabled" as const;
export const GAMIFICATION_ROULETTE_KEY =
  "gamification.roulette_enabled" as const;
export const GAMIFICATION_DAILY_LOGIN_KEY =
  "gamification.daily_login_enabled" as const;
export const GAMIFICATION_BALLOONS_KEY =
  "gamification.marketing_balloons_enabled" as const;

const STORAGE_KEY = "gamification.flags";
const REFRESH_INTERVAL_MS = 30_000;

export interface GamificationFlags {
  readonly enabled: boolean;
  readonly rouletteEnabled: boolean;
  readonly dailyLoginEnabled: boolean;
  readonly marketingBalloonsEnabled: boolean;
}

export const GAMIFICATION_FLAGS_ALL_OFF: GamificationFlags = {
  enabled: false,
  rouletteEnabled: false,
  dailyLoginEnabled: false,
  marketingBalloonsEnabled: false,
};

/** Pure projection: a key is on ONLY when the raw map carries it as `true`.
 * Boot callers must reject a missing response before invoking this projection. */
export function parseGamificationFlags(
  raw: Record<string, boolean> | null | undefined,
): GamificationFlags {
  return {
    enabled: raw?.[GAMIFICATION_ENABLED_KEY] === true,
    rouletteEnabled: raw?.[GAMIFICATION_ROULETTE_KEY] === true,
    dailyLoginEnabled: raw?.[GAMIFICATION_DAILY_LOGIN_KEY] === true,
    marketingBalloonsEnabled: raw?.[GAMIFICATION_BALLOONS_KEY] === true,
  };
}

function readPersisted(): GamificationFlags {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = JSON.parse(raw as string) as Partial<GamificationFlags>;
    return {
      enabled: parsed.enabled === true,
      rouletteEnabled: parsed.rouletteEnabled === true,
      dailyLoginEnabled: parsed.dailyLoginEnabled === true,
      marketingBalloonsEnabled: parsed.marketingBalloonsEnabled === true,
    };
  } catch {
    return GAMIFICATION_FLAGS_ALL_OFF;
  }
}

function persist(flags: GamificationFlags): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
  } catch {
    // quota / private-browsing restriction — ignore (mirrors persistSurfaceMode)
  }
}

function sameFlags(a: GamificationFlags, b: GamificationFlags): boolean {
  return (
    a.enabled === b.enabled &&
    a.rouletteEnabled === b.rouletteEnabled &&
    a.dailyLoginEnabled === b.dailyLoginEnabled &&
    a.marketingBalloonsEnabled === b.marketingBalloonsEnabled
  );
}

let _flags = readPersisted();
// Persisted values are only a boot hint. Integrated surfaces must wait for
// `_authoritative` before querying per-user access or painting gamification;
// a stale enabled snapshot must never become visible UI (#232).
let _authoritative = false;
const _listeners = new Set<() => void>();
let _refreshTimer: number | null = null;
let _refreshInFlight: Promise<boolean> | null = null;
let _lifecycleRefreshQueued = false;

export function setGamificationFlagsCache(flags: GamificationFlags): void {
  // Persist even when unchanged so a returning user's snapshot self-corrects
  // when the server flips a key (e.g. ops rolls gamification back off).
  persist(flags);
  const authorityChanged = !_authoritative;
  _authoritative = true;
  if (sameFlags(flags, _flags) && !authorityChanged) return;
  _flags = flags;
  for (const listener of _listeners) listener();
}

/** Apply only a successful boot response, preserving the last good snapshot
 * when the shared public-flags request fails or times out. */
export function applyGamificationFlagsFromBootFetch(
  raw: Record<string, boolean> | null | undefined,
): void {
  if (raw === null || raw === undefined) return;
  setGamificationFlagsCache(parseGamificationFlags(raw));
}

/** Refresh the shared snapshot after an admin mutation or lifecycle event. */
export function refreshGamificationFlagsCache(
  fetchImpl: typeof fetch = fetch,
): Promise<boolean> {
  if (_refreshInFlight !== null) return _refreshInFlight;
  const request = fetchAndApplyGamificationFlags(fetchImpl);
  _refreshInFlight = request;
  void request.then(
    () => clearCompletedRefresh(request),
    () => clearCompletedRefresh(request),
  );
  return request;
}

async function fetchAndApplyGamificationFlags(
  fetchImpl: typeof fetch,
): Promise<boolean> {
  const raw = await fetchPublicFeatureFlagsRaw(fetchImpl);
  if (raw === null) return false;
  setGamificationFlagsCache(parseGamificationFlags(raw));
  return true;
}

function clearCompletedRefresh(request: Promise<boolean>): void {
  if (_refreshInFlight === request) _refreshInFlight = null;
}

/** Reads the boot snapshot (seeded from localStorage, then updated by the boot
 *  fetch via main.tsx). */
export function readGamificationFlags(): GamificationFlags {
  return _flags;
}

/** True only after this document received a current public-flags response. */
export function readGamificationFlagsAuthoritative(): boolean {
  return _authoritative;
}

/** Subscribe to flag changes — the `useSyncExternalStore` contract. Fires when
 *  the boot fetch flips the cache. Returns an unsubscribe fn. */
export function subscribeGamificationFlags(listener: () => void): () => void {
  _listeners.add(listener);
  startLiveRefresh();
  return () => {
    _listeners.delete(listener);
    if (_listeners.size === 0) stopLiveRefresh();
  };
}

function startLiveRefresh(): void {
  if (typeof window === "undefined" || _refreshTimer !== null) return;
  _refreshTimer = window.setInterval(() => {
    if (document.visibilityState !== "hidden")
      void refreshGamificationFlagsCache();
  }, REFRESH_INTERVAL_MS);
  window.addEventListener("focus", queueLifecycleRefresh);
  document.addEventListener("visibilitychange", refreshAfterVisibilityChange);
}

function stopLiveRefresh(): void {
  if (_refreshTimer !== null) clearInterval(_refreshTimer);
  _refreshTimer = null;
  if (typeof window === "undefined") return;
  window.removeEventListener("focus", queueLifecycleRefresh);
  document.removeEventListener(
    "visibilitychange",
    refreshAfterVisibilityChange,
  );
}

function queueLifecycleRefresh(): void {
  if (document.visibilityState === "hidden" || _lifecycleRefreshQueued) return;
  _lifecycleRefreshQueued = true;
  queueMicrotask(runQueuedLifecycleRefresh);
}

function runQueuedLifecycleRefresh(): void {
  _lifecycleRefreshQueued = false;
  if (_listeners.size === 0 || document.visibilityState === "hidden") return;
  void refreshGamificationFlagsCache();
}

function refreshAfterVisibilityChange(): void {
  queueLifecycleRefresh();
}

/** Test-only: reset cache + listeners + persisted snapshot to all-off. */
export function resetGamificationFlagsForTests(): void {
  _flags = GAMIFICATION_FLAGS_ALL_OFF;
  _authoritative = false;
  _refreshInFlight = null;
  _lifecycleRefreshQueued = false;
  _listeners.clear();
  stopLiveRefresh();
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Test-only: emulate the synchronous persisted hint before boot authority. */
export function setGamificationFlagsHintForTests(
  flags: GamificationFlags,
): void {
  _flags = flags;
  _authoritative = false;
  for (const listener of _listeners) listener();
}
